import {
  AttachMoney,
  Category,
  CheckCircle,
  Inventory,
  TrendingUp,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { PricingStatistics } from "../../../types/admin";

interface PricingStatisticsCardsProps {
  statistics: PricingStatistics | null;
  loading: boolean;
}

const PricingStatisticsCards: React.FC<PricingStatisticsCardsProps> = ({
  statistics,
  loading,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6, 7].map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!statistics) {
    return null;
  }

  const cards = [
    {
      title: t("admin.pricing.totalCategoryPricing"),
      value: statistics.totalCategoryPricing,
      icon: <Category color="primary" />,
      color: "primary.main",
    },
    {
      title: t("admin.pricing.activeCategoryPricing"),
      value: statistics.activeCategoryPricing,
      icon: <CheckCircle color="success" />,
      color: "success.main",
    },
    {
      title: t("admin.pricing.totalProductPricing"),
      value: statistics.totalProductPricing,
      icon: <Inventory color="info" />,
      color: "info.main",
    },
    {
      title: t("admin.pricing.activeProductPricing"),
      value: statistics.activeProductPricing,
      icon: <CheckCircle color="success" />,
      color: "success.main",
    },
    {
      title: t("admin.pricing.fixedPricingRules"),
      value: statistics.fixedPricingCount,
      icon: <AttachMoney color="warning" />,
      color: "warning.main",
    },
    {
      title: t("admin.pricing.perKgPricingRules"),
      value: statistics.perKgPricingCount,
      icon: <TrendingUp color="secondary" />,
      color: "secondary.main",
    },
    {
      title: t("admin.pricing.perSizePricingRules"),
      value: statistics.perSizePricingCount,
      icon: <TrendingUp color="secondary" />,
      color: "secondary.main",
    },
  ];

  return (
    <Grid container spacing={3}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Card
            sx={{
              height: "100%",
              transition:
                "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 4,
              },
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: `${card.color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
              <Typography
                variant="h4"
                component="div"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                {card.value}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {card.title}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default PricingStatisticsCards;
